import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { InSeason2027 } from '@sorare/core/src/atoms/icons/SeasonalityIcon/InSeason.tsx';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandXXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { ANY_SPORT_ONBOARDING_PRO } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import {
  LIFECYCLE,
  useLifecycle,
} from '@sorare/core/src/hooks/useLifecycle.ts';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { css } from '@sorare/css';

import type { ProOnboardingBadge_cardSet } from './__generated__/index.graphql.ts';
import bannerShape from './assets/bridge-banner.webp';
import players from './assets/pro-players-cutout.webp';

const AVATAR_SIZE = 'calc(6 * var(--unit))';
export const PREVIEW_DURATION_MS = 2000;

const chipSlot = css`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  pointer-events: none;
`;

const avatarButton = css`
  position: relative;
  z-index: 1;
  display: flex;
  width: ${AVATAR_SIZE};
  height: ${AVATAR_SIZE};
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(var(--c-rgb-white), 0.17);
  box-shadow: inset 0 0 var(--double-unit) rgba(var(--c-rgb-blue-400), 0.6);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const banner = css`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 0;
  display: flex;
  align-items: center;
  gap: var(--half-unit);
  width: max-content;
  height: ${AVATAR_SIZE};
  padding-left: calc(${AVATAR_SIZE} + var(--double-unit));
  padding-right: var(--triple-unit);
  text-decoration: none;
  background: url(${bannerShape}) no-repeat center / 100% 100%;
  clip-path: inset(0 0 0 0 round 24px 0 0 24px);
  transition: all 0.45s cubic-bezier(0.03, 0.42, 0.47, 0.97);
`;

const bannerCollapsed = css`
  clip-path: inset(0 100% 0 0 round 24px 0 0 24px);
  opacity: 0;
  pointer-events: none;
`;

const messages = defineMessages({
  expand: { defaultMessage: 'Show pro mode' },
  collapse: { defaultMessage: 'Hide pro mode' },
});

type Props = {
  cardSet?: Nullable<ProOnboardingBadge_cardSet>;
};

export const ProOnboardingBadge = ({ cardSet }: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const colors = cardSet?.branding?.colors;
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const seen = !!lifecycle?.[LIFECYCLE.sawBridgeToProBanner];
  const [manuallyExpanded, setManuallyExpanded] = useState<boolean>();
  const [previewOver, setPreviewOver] = useState(false);

  const expanded = manuallyExpanded ?? (!seen && !previewOver);
  const collapsed = !expanded;

  useEffect(() => {
    if (seen || previewOver || manuallyExpanded !== undefined) {
      return undefined;
    }

    const timeout = setTimeout(() => setPreviewOver(true), PREVIEW_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [seen, previewOver, manuallyExpanded]);

  const markSeen = () => {
    if (!seen) {
      updateLifecycle(LIFECYCLE.sawBridgeToProBanner, true);
    }
  };

  return (
    <Horizontal className="isolate relative">
      <ButtonBase
        className={classNames(banner, { [bannerCollapsed]: collapsed })}
        to={generateSportContextPath(ANY_SPORT_ONBOARDING_PRO)}
        onClick={markSeen}
        tabIndex={collapsed ? -1 : undefined}
        aria-hidden={collapsed}
      >
        <InSeason2027 size={2.5} color="var(--c-white)" />
        <BrandXXS as="span" color="var(--c-white)">
          <FormattedMessage
            defaultMessage="Unlock{br}pro mode"
            values={{ br: <br /> }}
          />
        </BrandXXS>
      </ButtonBase>
      <ButtonBase
        className={avatarButton}
        style={{
          background:
            colors?.surfacePrimaryBackground ?? 'var(--c-nd-50-opaque)',
        }}
        aria-label={formatMessage(
          collapsed ? messages.expand : messages.collapse
        )}
        onClick={() => {
          markSeen();
          setManuallyExpanded(!expanded);
        }}
      >
        <img src={players} alt="" role="presentation" />
      </ButtonBase>
      {!seen && (
        <div className={chipSlot}>
          <Chip
            size="compact"
            brand
            color="brand"
            style={{ background: 'var(--c-blue-400)' }}
          >
            <FormattedMessage {...glossary.new} />
          </Chip>
        </div>
      )}
    </Horizontal>
  );
};

ProOnboardingBadge.fragments = {
  cardSet: gql`
    fragment ProOnboardingBadge_cardSet on CardSet {
      slug
      branding {
        id
        colors {
          surfacePrimaryBackground
        }
      }
    }
  ` as TypedDocumentNode<ProOnboardingBadge_cardSet>,
};
